import React, { useEffect, useMemo, useState } from 'react'
import apiClient from '../../../utils/apiclient'

const formatDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

const AddOnOrders = ({ route = '/addon/orders' }) => {
  const [orders, setOrders] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true)
      setError('')

      try {
        const response = await apiClient.get(route)
        const responseOrders = Array.isArray(response.data)
          ? response.data
          : response.data?.content

        if (!Array.isArray(responseOrders)) {
          throw new Error('The add-on orders response was not a list.')
        }

        setOrders(responseOrders)
      } catch (requestError) {
        console.error('Unable to fetch add-on orders:', requestError)
        setError('Unable to load add-on orders. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [route])

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return orders

    return orders.filter((order) => {
      const orderDetails = [
        order.id,
        order.status,
        order.razorpayOrderId,
        order.razorpayPaymentId,
        order.createdOn,
        order.subscriptionOrderId,
        order.subscriptionId,
        ...(order.items || []).flatMap(({ item = {} }) => [
          item.name,
          item.restroname,
          item.publiclunchDescription,
          item.publicdinnerDescription,
        ]),
      ]

      return orderDetails.some((value) =>
        String(value ?? '').toLowerCase().includes(query)
      )
    })
  }, [orders, search])

  return (
    <div className="w-full flex-1 flex flex-col min-h-0">
      <div className="flex justify-between items-center mb-2 flex-shrink-0 gap-3">
        <h2 className="text-base md:text-xl font-semibold text-gray-800 whitespace-nowrap">
          Add-on Orders
        </h2>
        <input
          className="flex-1 border-2 py-1.5 px-3 md:py-2 md:px-4 text-sm md:text-base rounded-lg outline-none focus:border-blue-500 min-w-0"
          placeholder="Search orders or items..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="w-full flex-1 overflow-auto pb-4 relative">
        {loading ? (
          <div className="flex justify-center items-center p-8 text-gray-600">
            Loading add-on orders...
          </div>
        ) : error ? (
          <div role="alert" className="p-4 text-center text-red-700">
            {error}
          </div>
        ) : (
          <table className="min-w-max w-full bg-white shadow-md border-collapse">
            <thead className="bg-gray-800 text-white sticky top-0 z-20 shadow-md">
              <tr>
                {[
                  'Add-on Order ID',
                  'Subscription Order ID',
                  'Subscription ID',
                  'Items',
                  'Order Price',
                  'Status',
                  'Razorpay Order ID',
                  'Razorpay Payment ID',
                  'Created On',
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-2 md:px-5 py-2 md:py-3 text-[10px] md:text-sm whitespace-nowrap"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => {
                const items = Array.isArray(order.items) ? order.items : []

                return (
                  <tr
                    key={order.id ?? index}
                    className="border border-gray-300 hover:bg-gray-50 align-top"
                  >
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">
                      {order.id ?? '-'}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">
                      {order.subscriptionOrderId ?? '-'}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">
                      {order.subscriptionId ?? '-'}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm min-w-[360px]">
                      {items.length ? (
                        <div className="space-y-3">
                          {items.map((orderItem, itemIndex) => {
                            const item = orderItem.item || {}
                            return (
                              <div
                                key={orderItem.id ?? item.id ?? itemIndex}
                                className="border-b last:border-b-0 pb-2 last:pb-0"
                              >
                                <div className="font-semibold">
                                  {item.name || 'Unnamed item'} × {orderItem.count ?? 0}
                                  {' · '}₹{item.price ?? '-'} each
                                </div>
                                <div className="text-gray-600">
                                  Restaurant: {item.restroname || '-'}
                                  {' · '}Item total: ₹
                                  {Number.isFinite(Number(item.price)) && Number.isFinite(Number(orderItem.count))
                                    ? (Number(item.price) * Number(orderItem.count)).toFixed(2)
                                    : '-'}
                                </div>
                                {item.description && (
                                  <div className="text-gray-600">Description: {item.description}</div>
                                )}
                                {item.publiclunchDescription && (
                                  <div className="text-gray-600">
                                    Lunch: {item.publiclunchDescription}
                                  </div>
                                )}
                                {item.publicdinnerDescription && (
                                  <div className="text-gray-600">
                                    Dinner: {item.publicdinnerDescription}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center whitespace-nowrap">
                      ₹{order.price ?? '-'}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center whitespace-nowrap">
                      {order.status || '-'}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">
                      {order.razorpayOrderId || '-'}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center">
                      {order.razorpayPaymentId || '-'}
                    </td>
                    <td className="px-2 md:px-5 py-2 text-[10px] md:text-sm text-center whitespace-nowrap">
                      {formatDate(order.createdOn)}
                    </td>
                  </tr>
                )
              })}
              {filteredOrders.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    {orders.length ? 'No add-on orders match your search.' : 'No add-on orders found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default AddOnOrders
